import React, { useState, useEffect, useMemo } from 'react';
import { PagingQuery } from 'tea-component';
import { getCountryFlag } from '@/pages/home/_config/countries';
import FirstRow from '@/pages/intelligence/components/TableComponents/components/FirstRow';
import { TopchartTableProps } from '@/pages/intelligence/pages/_config/topchartsInterface';
import { useIntl, useHistory } from 'umi';
import TopchartTable from '@/pages/intelligence/components/TableComponents/TopchartTable';
import { SortByItemType } from '@/pages/intelligence/components/TableComponents/components/SortBy';
import CloumsCenter from '@/pages/intelligence/components/TableComponents/components/CloumsCenter';
import {
  RetentionMetricColumn,
  getRetentionMonthlySensortowerDxHoverContent,
  retentionOptions,
} from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/RetentionCloums';
import { getGenreColumns } from '@/pages/intelligence/v3/utils/topcharts';
import { AppMagicGenreTagType } from '@intelligence/components/TableComponents/components/AppMagicGenreTagList';
import {
  TableCheckboxHeader,
  TableCheckboxRender,
} from '@/pages/intelligence/components/TableComponents/components/TableCheckbox';
import { getDetailGamePathCommon } from '@/pages/intelligence/utils/topchartsUtils';
import { DetailsCloums } from '@/pages/intelligence/pages/TopCharts/_config/TableCloums/CommonCloums';
import { changeSingleTimeToRange } from '@intelligence/utils/filterUtils';
import useFilterExtend from '@/pages/intelligence/hooks/useFilterExtend';
import { getGameRankDetails } from '@intelligence/utils/tableUtils';
import { formatReleaseTime } from '@home/components/NavHeader/sreachContext/utils';
import { getTagFilterColumns } from '@intelligence/v3/utils/sensorTowerTagColumns';

interface RetentionTableProps extends TopchartTableProps {
  genreSource?: string;
  entityType?: string;
  filterValue?: IRecord<any>;
  gameDetailsLoading?: boolean;
  gameDetailsMapping?: IRecord<IRecord>;
  onTagClick?: (type: string, tag: string) => void;
  compareDateAttr?: {
    dateType?: string;
    startDate?: string;
    endDate?: string;
  };
  onAppMagicTagClick?: (
    type: AppMagicGenreTagType,
    superTag: string,
    majorTag?: string,
    midTag?: string,
    subTag?: string,
  ) => void;
  pageId?: string;
}

export default function RetentionTable(props: RetentionTableProps) {
  const {
    loading,
    records = [],
    tableTotal = 0,
    pageSortAttr,
    tableSelection,
    tableSelectionAll,
    onTableSortChage,
    onTablePagingChange,
    onTableCheckGame,
    genreSource = 'annie',
    entityType = '',
    compareDateAttr,
    onAppMagicTagClick,
    filterValue,
    gameDetailsLoading = true,
    gameDetailsMapping = {},
    onTagClick,
    pageId,
  } = props;

  const { filterExtend, setFilterExtend, isOperate } = useFilterExtend({ pageId });
  const { locale, formatMessage } = useIntl(); // 中英切换方法
  const history = useHistory();
  const {
    tablePageIndex,
    tablePageSize,
    currentSortValue,
    currentSortMetric,
    currentRatioType,
    currentOrderSource,
  } = pageSortAttr || {};
  const defaultPageSize = 100;
  const [pageIndex, setPageIndex] = useState(tablePageIndex || 1);
  const [pageSize, setPageSize] = useState(tablePageSize || defaultPageSize);
  const [selectList, setSelectList] = useState(tableSelection);

  useEffect(() => {
    setPageIndex(tablePageIndex);
    setPageSize(tablePageSize);
  }, [tablePageIndex, tablePageSize]);

  const onPagingChange = (e: PagingQuery) => {
    setPageIndex(e.pageIndex || 1);
    setPageSize(e.pageSize || defaultPageSize);
    onTablePagingChange(e);
  };

  const onSortChage = (e: SortByItemType) => {
    onTableSortChage(e);
  };

  useEffect(() => {
    setSelectList(tableSelection);
  }, [tableSelection]);

  const getMetricCloums = () => {
    const metricCloums: any[] = [];
    retentionOptions.forEach((t) => {
      metricCloums.push(
        RetentionMetricColumn({
          dataSource: 'sensortower',
          title: t.title,
          titleKey: t?.titleKey,
          textContent:
            t.key === 'mau'
              ? t.textContent
              : getRetentionMonthlySensortowerDxHoverContent(
                  formatMessage,
                  t.key,
                ),
          key: `${t.key}`,
          sortMetric: t.key,
          metricKey: t?.metricKey || t.key,
          currentSortValue: currentSortValue,
          currentSortKey: `${currentSortMetric}_${currentRatioType}`,
          onSortChage: onSortChage,
          entityType,
          dateType: compareDateAttr?.dateType,
          startDate: compareDateAttr?.startDate,
          endDate: compareDateAttr?.endDate,
        }),
      );
    });
    return metricCloums;
  };

  const getDetailGamePath = (gameId: string) => {
    return getDetailGamePathCommon({
      entityType: 'mobile',
      gameId: gameId,
      tab: 'stats',
      subTab: 'retention_monthly_sensortower',
    });
  };

  const onClickGame = (gameId: string) => {
    if (isOperate) {
      const updateTimeFilter = changeSingleTimeToRange({
        timeType: 'month',
        subtractNum: 5,
        filterExtend: filterExtend,
      });
      setFilterExtend(updateTimeFilter);
    }
    history.push(getDetailGamePath(gameId));
  };

  const columns = useMemo(
    () => [
      {
        key: 'select',
        width: 36,
        fixed: 'left',
        header: () => (
          <TableCheckboxHeader
            selectList={selectList}
            tableSelectionAll={tableSelectionAll}
            disabled={loading || records.length === 0}
            onChange={(list, stats) => {
              onTableCheckGame(list, stats);
              setSelectList(list);
            }}
          />
        ),
        render: (cvm: any) => (
          <TableCheckboxRender
            selectList={selectList}
            cvm={{ ...(cvm || {}), gameId: cvm?.id }}
            disabled={getGameRankDetails(cvm?.rank).isDisabled}
            onChange={(list, status) => {
              setSelectList(list);
              onTableCheckGame(list, status);
            }}
          />
        ),
      },
      {
        key: 'gametitle',
        header: (
          <div className="dbi-firstRow-flex">
            <div className="dbi-text-id">#</div>
            <div>{formatMessage({ id: 'dbi_top_gametitle' })}</div>
          </div>
        ),
        width: 330,
        fixed: 'left',
        render: (cvm: any) => (
          <FirstRow
            id={getGameRankDetails(cvm?.rank)?.rankText}
            cvm={{
              img: cvm?.cover,
              gametitle: cvm?.name,
              countryIcon: getCountryFlag(cvm?.publisher_addr),
              text: cvm?.publisher,
            }}
            rihgtClickToJumpHref={getDetailGamePath(cvm?.id)}
            onClick={() => {
              onClickGame(cvm.id);
            }}
          />
        ),
      },
      ...getMetricCloums(),
      {
        key: 'release_time',
        header: (
          <CloumsCenter width={140} align="left">
            <div className="header-other">
              {formatMessage({ id: 'dbi_release_date' })}
            </div>
          </CloumsCenter>
        ),
        align: 'center',
        width: 140,
        render: (cvm: any) => {
          const releaseTime = formatReleaseTime(locale, {
            isSpecialReleaseTime: cvm?.is_special_release_time,
            releaseTime: cvm?.release_time,
            releaseTimeEN: cvm?.release_time_en,
            releaseTimeCN: cvm?.release_time_cn,
          });
          return (
            <CloumsCenter width={140} align="left">
              {releaseTime}
            </CloumsCenter>
          );
        },
      },
      ...getGenreColumns({
        isGameDetailsMode: false,
        genreSource: genreSource as any,
        formatMessage,
        onAppMagicTagClick,
      }),
      ...getTagFilterColumns({
        filterValue,
        gameDetailsLoading,
        gameDetailsMapping,
        locale,
        formatMessage,
        onTagClick,
      }),
      DetailsCloums({
        onClickGame: (cvm: any) => {
          onClickGame(cvm?.id);
        },
        formatMessage,
      }),
    ],
    [
      selectList,
      tableSelectionAll,
      loading,
      records.length,
      formatMessage,
      locale,
      genreSource,
      onAppMagicTagClick,
      filterValue,
      gameDetailsLoading,
      gameDetailsMapping,
      onTagClick,
      currentSortValue,
      currentSortMetric,
      currentRatioType,
      entityType,
    ],
  );

  return (
    <>
      <TopchartTable
        recordKey={'id'}
        loading={loading}
        records={records}
        tableTotal={tableTotal}
        pageIndex={pageIndex}
        pageSize={pageSize}
        onPagingChange={onPagingChange}
        columns={columns}
      />
    </>
  );
}
